import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, makeStateKey, StateKey, TransferState } from '@angular/core';
import { Router } from '@angular/router';
import { ApiResponse } from '@shared/models/api-response.model';
import { Languages } from '@shared/models/languages.model';
import { DomainService } from '@core/services/domain.service';
import { GeneralService } from '@core/services/general.service';
import { StaticDataService } from '@core/services/static-data.service';
import { EndDateTimeStateKey, RemainingTimeStateKey, StartDateTimeStateKey, UnderMaintenanceStateKey } from '@shared/transfer-state.key';
import { PlatformType } from '@shared/types/platform.type';
import { BehaviorSubject, catchError, EMPTY, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceHttpService {

  private languages: Languages;
  private underMaintenance: boolean;
  private underMaintenanceSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private startDateTime: string;
  private endDateTime: string;
  private remainingTime: string;

  private underMaintenanceStateKey: StateKey<boolean> = makeStateKey<boolean>(UnderMaintenanceStateKey); /* For currentDomainSet transfer state use */
  private startDateTimeStateKey: StateKey<string> = makeStateKey<string>(StartDateTimeStateKey); /* For startDateTime transfer state use */
  private endDateTimeStateKey: StateKey<string> = makeStateKey<string>(EndDateTimeStateKey); /* For endDateTime transfer state use */
  private remainingTimeStateKey: StateKey<string> = makeStateKey<string>(RemainingTimeStateKey); /* For remainingTime transfer state use */

  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    private router: Router,
    private staticDataService: StaticDataService,
    private generalService: GeneralService,
    private domainService: DomainService
  ) {
    this.languages = this.staticDataService.getLanguages();
  }

  checkMaintenance(platform: PlatformType): Promise<boolean> {
    console.debug(`--- Start: checkMaintenance (${platform}) ---`);
    return new Promise((resolve, reject) => {

      /* Client Browser */
      if (platform == 'browser') {
        this.setUnderMaintenance(this.transferState.get(this.underMaintenanceStateKey, false));
        if (this.underMaintenance) {
          this.startDateTime = this.transferState.get(this.startDateTimeStateKey, null);
          this.endDateTime = this.transferState.get(this.endDateTimeStateKey, null);
          this.remainingTime = this.transferState.get(this.remainingTimeStateKey, null);

          this.domainService.setLocale(platform, `${this.generalService.getClientBrowserLanguage() in this.languages ? this.generalService.getClientBrowserLanguage() : 'en'}-my`);

          this.router.navigate(['maintenance']);
        }
        console.debug("Under Maintenance: " + this.underMaintenance);
        console.debug(`--- End  : setDomainAndPath (${platform}) ---\n\n`);
        resolve(!this.underMaintenance);
      }
      /* Server */
      else {
        this.http.get<ApiResponse>(`/settingsystem/maintenance`).pipe(
          tap(res => {
            console.debug(`--- End  : checkMaintenance (${platform}) ---\n\n`);
            this.underMaintenance = false;
            this.transferState.set(this.underMaintenanceStateKey, this.underMaintenance);
            resolve(true)
          }),
          catchError((err: HttpErrorResponse) => {
            const rows = err?.error?.data?.rows;
            const isMaintenance = err?.status === 503 && rows != null;

            if (isMaintenance) {
              this.underMaintenance = true;
              this.startDateTime = rows.start_maintenance_time;
              this.endDateTime = rows.end_maintenance_time;
              this.remainingTime = rows.remaining_time;

              this.transferState.set(this.underMaintenanceStateKey, this.underMaintenance);
              this.transferState.set(this.startDateTimeStateKey, this.startDateTime);
              this.transferState.set(this.endDateTimeStateKey, this.endDateTime);
              this.transferState.set(this.remainingTimeStateKey, this.remainingTime);

              this.domainService.setLocale(platform, `${this.generalService.getClientBrowserLanguage() in this.languages ? this.generalService.getClientBrowserLanguage() : 'en'}-my`);

              console.error('Server Call API Error : checkMaintenance (under maintenance)');
              console.debug(`--- End  : checkMaintenance (${platform}) ---\n\n`);
              resolve(false);
            } else {
              this.underMaintenance = false;
              this.transferState.set(this.underMaintenanceStateKey, this.underMaintenance);
              console.error('Server Call API Error : checkMaintenance', err?.status, err?.message ?? err);
              console.debug(`--- End  : checkMaintenance (${platform}) ---\n\n`);
              resolve(true);
            }
            return EMPTY;
          })
        ).subscribe();
      }
    });
  }

  getUnderMaintenance(): boolean {
    return this.underMaintenance;
  }

  setUnderMaintenance(underMaintenance: boolean) {
    this.underMaintenance = underMaintenance;
    this.underMaintenanceSubject.next(this.underMaintenance);
    this.domainService.setCurrentPath('/maintenance')
  }

  getStartDateTime(): string {
    return this.startDateTime;
  }

  setStartDateTime(startDateTime: string) {
    this.startDateTime = startDateTime;
  }

  getEndDateTime(): string {
    return this.endDateTime;
  }

  setEndDateTime(endDateTime: string) {
    this.endDateTime = endDateTime;
  }

  getRemainingTime(): string {
    return this.remainingTime;
  }

  setRemainingTime(remainingTime: string): void {
    this.remainingTime = remainingTime;
  }

  getUnderMaintenanceSubject(): BehaviorSubject<boolean> {
    return this.underMaintenanceSubject;
  }
}